import React, { useCallback, useContext, useEffect, useState } from "react";

import PsychologyIcon from "@mui/icons-material/Psychology";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DownloadCloud } from "react-feather";
import { useParams } from "react-router-dom";

import { DiagnosticFile } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import AnalysisDialog from "./analysis-dialog";
import { getUserDiagnosticFiles } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { UserUrlParamTypes } from "types/user-display";

interface ParsedFilename {
  timestamp: string;
  originalName: string;
}

const parseFilename = (filename: string): ParsedFilename => {
  const parts = filename.split("_");
  if (parts.length >= 3) {
    const timestamp = parts[2].replace(".zip", "");
    return { timestamp, originalName: filename };
  }
  return { timestamp: "", originalName: filename };
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
};

const UserDiagsTab = () => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [isLoading, setIsLoading] = useState(false);
  const [diagFiles, setDiagFiles] = useState<DiagnosticFile[]>([]);
  const { showError } = useContext(SnackbarContext);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DiagnosticFile | undefined>(
    undefined,
  );

  const handleOpenAnalysisDialog = (diagFile: DiagnosticFile) => {
    setSelectedFile(diagFile);
    setAnalysisDialogOpen(true);
  };

  const handleCloseAnalysisDialog = () => {
    setAnalysisDialogOpen(false);
    setSelectedFile(undefined);
  };

  const loadAllDiagnosticFiles = useCallback(() => {
    setIsLoading(true);
    getUserDiagnosticFiles(
      {
        userId,
      },
      callOptionsHeaders(regionCode, userId),
    )
      .then((res) => {
        setDiagFiles(res.files);
        // us region - BSA-6793 workaround
        if (regionCode.toLowerCase() !== "us" && res.files.length == 0) {
          getUserDiagnosticFiles(
            {
              userId,
            },
            callOptionsHeaders("us", userId),
          ).then((res) => {
            setDiagFiles(res.files);
          });
        }
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [regionCode, userId, showError]);

  useEffect(() => {
    loadAllDiagnosticFiles();
  }, [loadAllDiagnosticFiles, regionCode, userId, showError]);

  if (isLoading) {
    return (
      <Box p={1}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "auto" }} style={{ padding: "10px" }}>
      <Alert variant="filled" severity="info">
        The files are only downloadable for a limited time for security reasons.
        Reload tab to reset timer.
      </Alert>
      {diagFiles.length === 0 ? (
        <Typography data-testid="no-diagnostic-files">
          No diagnostic files found for {userId}
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="diagnostic files table">
            <TableHead>
              <TableRow>
                <TableCell>Uploaded At</TableCell>
                <TableCell>File Name</TableCell>
                <TableCell>File Size</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diagFiles.map((diagFile: DiagnosticFile) => {
                const { timestamp, originalName } = parseFilename(
                  diagFile.filename,
                );
                // format the file size to MB
                const fileSize = (Number(diagFile.size) / 1024 / 1024).toFixed(
                  2,
                );
                return (
                  <TableRow
                    key={`diag-file-${diagFile.filename}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>{formatDate(timestamp)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: "bold" }}>
                        {originalName}
                      </Typography>
                    </TableCell>
                    <TableCell>{fileSize} MB</TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PsychologyIcon />}
                          onClick={() => handleOpenAnalysisDialog(diagFile)}
                          size="small"
                        >
                          Analyse
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<DownloadCloud />}
                          component="a"
                          href={diagFile.signedUrl}
                          target="_blank"
                          size="small"
                        >
                          Download
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {selectedFile && (
        <AnalysisDialog
          open={analysisDialogOpen}
          file={selectedFile}
          onClose={handleCloseAnalysisDialog}
        />
      )}
    </Box>
  );
};

export default UserDiagsTab;
